package com.arenaproplay.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.breakpoint.BreakpointSizes
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.di.ProvideAppContainer
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.state.auth.ProvideAuthSession
import com.arenaproplay.admin.ui.components.notifications.ProvideToast
import com.arenaproplay.admin.ui.localization.ProvideLanguage
import com.arenaproplay.admin.ui.localization.rememberLanguage
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.initAppTheme
import com.arenaproplay.admin.utils.Storage
import com.arenaproplay.admin.utils.StorageKeys
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@InitSilk
fun initSilk(ctx: InitSilkContext) {
    val savedTheme = try { Storage.get(StorageKeys.THEME) } catch (_: Exception) { null }
    ctx.config.initialColorMode = when (savedTheme) {
        "LIGHT" -> ColorMode.LIGHT
        else -> ColorMode.DARK
    }

    ctx.theme.breakpoints = BreakpointSizes(
        sm = 0.px,
        md = 768.px,
        lg = 1280.px,
        xl = 1920.px
    )

    initAppTheme(ctx)
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    ProvideAppContainer {
        val languageState = rememberLanguage()
        key("silk-app") {
            SilkApp {
                ProvideLanguage(languageState) {
                    ProvideAuthSession {
                        // Rehydrate authenticated HTTP client when the page reloads
                        // with a stored session — otherwise AppContainer.adminRepository
                        // stays on the mock impl and the dashboard never calls /admin/*.
                        val session = LocalAuthSession.current
                        val container = LocalAppContainer.current
                        // Keyed on session *presence*, not the token value: the Ktor Auth
                        // plugin refreshes tokens inside the client on its own, and
                        // recreating the client on every 15-min access-token rotation
                        // closes it under in-flight requests and long-lived pollers
                        // (JobCancellationException in e.g. the Topbar online badge).
                        LaunchedEffect(session.tokens != null) {
                            val tokens = session.tokens
                            if (tokens != null) {
                                container.createAuthenticatedClients(
                                    accessToken = tokens.accessToken,
                                    refreshToken = tokens.refreshToken,
                                    onTokensRefreshed = { newAccess, newRefresh ->
                                        session.updateTokens(newAccess, newRefresh)
                                    },
                                    onAuthFailed = {
                                        session.signOut()
                                    },
                                )
                            } else {
                                container.clearAuthenticatedClients()
                            }
                        }

                        val colorMode = ColorMode.current
                        val colors = AppColors.getColors(colorMode)
                        Surface(
                            SmoothColorStyle.toModifier()
                                // body уже 100dvh; 100vh добавлял высоту за адресной строкой на мобиле
                                .minHeight(100.percent)
                                .scrollBehavior(ScrollBehavior.Smooth)
                                .backgroundColor(colors.background)
                                .color(colors.textPrimary)
                        ) {
                            ProvideToast {
                                content()
                            }
                        }
                    }
                }
            }
        }
    }
}
